import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { Action } from '../../actions';
import Utils, { thumbnailsFolder, downloadsFolder, iconsFolder } from '../../utils';
import { Api } from '../../typings/api';
import UserAgentUtils from '../../utils/userAgent';
import DriveItemUtils from '../../utils/driveItem';
import NativeApi, { DownloadPriority } from '../../utils/nativeApi';
import { DownloadStatus, OfflineItemStatus, LogoThumbState, ThumbType, BrandThumbConfigLocation, LogoType } from './reducer';
import { getAllDriveItems, receiveDriveItems, receiveResolutions, getOrFetchDriveItemMeta } from '../DriveItemList/actions';
import { DriveItemInfo } from '../DriveItemList/reducer';
import LocalStorageManager from '../../utils/localStorage/manager';
import { requestMobileAppDownload, deleteItem, deletePendingDownloads } from '../DownloadManager/actions';
import { DownloadManagerItemState } from '../DownloadManager/reducer';
import { DownloadMode } from '../DownloadManager/reducer';
import { MenuItemType } from '../../typings/menu';
import { createNotification } from '../NotificationBar/actions';
import { updateNewItemsStore } from '../DriveItemList/New/actions';

const Q = require('q');

enum OfflineManagerActionKeys {
	INIT_THUMB_MAP = '@@offlineManager/INIT_THUMB_MAP',
	INIT_REQUIRED_ITEMS = '@@offlineManager/INIT_REQUIRED_ITEMS',
	INIT_LAST_UPDATE_TIMESTAMP = '@@offlineManager/INIT_LAST_UPDATE_TIMESTAMP',
	INIT_BRAND_THUMB = '@@offlineManager/INIT_BRAND_THUMB',
	INIT_HOME_THUMB = '@@offlineManager/INIT_HOME_THUMB',
	START_INITIALIZATION = '@@offlineManager/START_INITIALIZATION',
	END_INITIALIZATION = '@@offlineManager/END_INITIALIZATION',
	INITIALIZATION_ERROR = '@@offlineManager/INITIALIZATION_ERROR',
	UPDATE_THUMB_STATUS = '@@offlineManager/UPDATE_THUMB_STATUS',
	UPDATE_REQUIRED_ITEM_STATUS = '@@offlineManager/UPDATE_REQUIRED_ITEM_STATUS',
	UPDATE_BRAND_THUMB = '@@offlineManager/UPDATE_BRAND_THUMB',
	REMOVE_REQUIRED_ITEM = '@@offlineManager/REMOVE_REQUIRED_ITEM',
	UPDATE_DOWNLOAD_THUMBS_STATUS = '@@offlineManager//UPDATE_DOWNLOAD_THUMBS_STATUS',
	UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS = '@@offlineManager//UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS'
}

interface InitThumbMapAction extends Action {
	type: OfflineManagerActionKeys.INIT_THUMB_MAP;
	thumbMap: { [k: number]: OfflineItemStatus };
}

interface InitRequiredItemsAction extends Action {
	type: OfflineManagerActionKeys.INIT_REQUIRED_ITEMS;
	requiredItems: { [k: number]: OfflineItemStatus };
}

interface InitLastUpdateTimestampAction extends Action {
	type: OfflineManagerActionKeys.INIT_LAST_UPDATE_TIMESTAMP;
	lastUpdateTimestamp: Date;
}

interface InitBrandThumbAction extends Action {
	type: OfflineManagerActionKeys.INIT_BRAND_THUMB;
	brandThumb: LogoThumbState;
}

interface InitHomeThumbAction extends Action {
	type: OfflineManagerActionKeys.INIT_HOME_THUMB;
	homeThumb: LogoThumbState;
}


interface StartInitializationAction extends Action {
	type: OfflineManagerActionKeys.START_INITIALIZATION;
}

interface EndInitializationAction extends Action {
	type: OfflineManagerActionKeys.END_INITIALIZATION;
}

interface InitializationErrorAction extends Action {
	type: OfflineManagerActionKeys.INITIALIZATION_ERROR;
}

interface UpdateThumbStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_THUMB_STATUS;
	driveItemId: number;
	version: number;
	status: DownloadStatus;
}

interface UpdateRequiredItemStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_REQUIRED_ITEM_STATUS;
	version: number;
	id: number;
	isIcon: boolean;
	url?: string;
	status: DownloadStatus;
}

interface UpdateBrandThumbStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_BRAND_THUMB;
	status: DownloadStatus;
	configLocation: BrandThumbConfigLocation;
	thumbType: ThumbType;
	filename?: string;
}

interface RemoveRequiredItemAction extends Action {
	type: OfflineManagerActionKeys.REMOVE_REQUIRED_ITEM;
	id: number;
}

interface UpdateDownloadThumbsStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_THUMBS_STATUS;
	isDownloading: boolean;
}

interface UpdateRequiredItemsStatusAction extends Action {
	type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS;
	isDownloading: boolean;
}

type OfflineManagerAction = InitThumbMapAction
	| InitRequiredItemsAction
	| InitLastUpdateTimestampAction
	| InitBrandThumbAction
	| InitHomeThumbAction
	| StartInitializationAction
	| EndInitializationAction
	| InitializationErrorAction
	| UpdateThumbStatusAction
	| UpdateRequiredItemStatusAction
	| UpdateBrandThumbStatusAction
	| RemoveRequiredItemAction
	| UpdateDownloadThumbsStatusAction
	| UpdateRequiredItemsStatusAction;

const loadOfflineManager = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const previousOfflineManagerState = LocalStorageManager.getStoreOfflineManager();

	if (previousOfflineManagerState) {
		const previousThumbMap = previousOfflineManagerState.thumbMap || {};
		const previousRequiredItems = previousOfflineManagerState.requiredItems || {};

		let newThumbMap = {};
		let newRequiredItems = {};

		Object.keys(previousThumbMap).forEach(key => {
			const driveItemId = parseInt(key, 10);
			const previousThumbStatus: OfflineItemStatus = previousThumbMap[driveItemId];

			if (previousThumbStatus.status === DownloadStatus.DOWNLOADED) {
				newThumbMap[driveItemId] = previousThumbStatus;
			}
		});

		Object.keys(previousRequiredItems).forEach(key => {
			const driveItemId = parseInt(key, 10);
			const previousRequiredItemStatus: OfflineItemStatus = previousRequiredItems[driveItemId];

			if (previousRequiredItemStatus.status === DownloadStatus.DOWNLOADED) {
				newRequiredItems[driveItemId] = previousRequiredItemStatus;
			}
		});

		dispatch({
			type: OfflineManagerActionKeys.INIT_THUMB_MAP,
			thumbMap: newThumbMap
		});

		dispatch({
			type: OfflineManagerActionKeys.INIT_REQUIRED_ITEMS,
			requiredItems: newRequiredItems
		});

		if (previousOfflineManagerState.lastUpdateTimestamp) {
			dispatch({
				type: OfflineManagerActionKeys.INIT_LAST_UPDATE_TIMESTAMP,
				lastUpdateTimestamp: new Date(previousOfflineManagerState.lastUpdateTimestamp)
			});
		}

		if (previousOfflineManagerState.brandThumb) {
			dispatch({
				type: OfflineManagerActionKeys.INIT_BRAND_THUMB,
				brandThumb: previousOfflineManagerState.brandThumb,
			});
		}
	}
};

const initializeOfflineManager = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): {} => {
	dispatch(startInitialization());
	const { galleryInfos } = getState();
	if (UserAgentUtils.isMobileApp) {
		if (UserAgentUtils.isOnline()) {

			const { template } = galleryInfos;
			const requiredItemsDatas = getRequiredItemsDatas(template);

			dispatch(loadDependenciesMetaFromLocalStorage());

			return dispatch(getAllDriveItems())
				.catch(() => {
					dispatch(createNotification('error.initialization_fail', { id: galleryInfos.config.applicationGallery }, -1));
					return dispatch(sendInitializationError());
				})
				.then(() => dispatch({
					type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS,
					isDownloading: true
				}))
				.then(() => NativeApi.existDirectory(downloadsFolder) as PromiseLike<boolean>)
				.then(directoryExist => directoryExist ? {} : NativeApi.createDirectory(downloadsFolder))
				.then(() => dispatch(downloadRequiredItems(requiredItemsDatas)))
				.then(() => {
					dispatch({
						type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_REQUIRED_ITEM_STATUS,
						isDownloading: false
					});
					dispatch({
						type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_THUMBS_STATUS,
						isDownloading: true
					});
				})
				.then(() => NativeApi.existDirectory(thumbnailsFolder) as PromiseLike<boolean>)
				.then(directoryExist => directoryExist ? {} : NativeApi.createDirectory(thumbnailsFolder))
				.then(() => dispatch(downloadBrandThumbIfNeeded(BrandThumbConfigLocation.REMOTE_CONFIG, template, false)))
				.then(() => dispatch(downloadDirectChildrenThumb(galleryInfos.config.applicationGallery, 0, [])))
				.then(() => dispatch(deletePendingDownloads()))
				.then(() => dispatch(deleteObsoleteItems(requiredItemsDatas)))
				.then(() => dispatch({
					type: OfflineManagerActionKeys.UPDATE_DOWNLOAD_THUMBS_STATUS,
					isDownloading: false
				}))
				.then(() => dispatch(endInitialization()));
		} else {
			const cachedDriveListState = LocalStorageManager.getStoreDriveList();
			if (cachedDriveListState) {
				const { driveItems, resolutions } = cachedDriveListState;
				dispatch(receiveDriveItems(driveItems));
				dispatch(receiveResolutions(resolutions));
				dispatch(updateNewItemsStore({}));
				dispatch(deletePendingDownloads());
			} else {
				dispatch(createNotification('info.offline_no_config', { id: galleryInfos.config.applicationGallery }, -1));
				return dispatch(sendInitializationError());
			}
		}
	}
	return dispatch(endInitialization());
};

const loadDependenciesMetaFromLocalStorage = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	// When online, we don't reuse old saved store an fetch all items in gallery.
	// We have to get the dependencies of downloaded items that are not in gallery from the saved drive items
	const cachedDriveListState = LocalStorageManager.getStoreDriveList();
	if (cachedDriveListState) {
		const { downloadManager } = getState();

		let dependencies: number[] = [];
		Object.keys(downloadManager.items).forEach(id => {
			const downloadItem: DownloadManagerItemState = downloadManager.items[id];
			dependencies = [...dependencies, ...downloadItem.dependencies];
		});
		// Remove duplicate entries
		dependencies = dependencies.reduce((dependencies: number[], dependencyId: number) => dependencies.includes(dependencyId) ? dependencies : [...dependencies, dependencyId], []);

		const dependenciesMeta = {};
		dependencies.forEach(dependencyId => {
			const driveItem: DriveItemInfo = cachedDriveListState.driveItems[dependencyId];

			if (driveItem && !driveItem.isInGallery) {
				dependenciesMeta[dependencyId] = driveItem;
			}
		});

		dispatch(receiveDriveItems(dependenciesMeta));
	}
 };

const getObsoleteOfflineItems = (offlineItemsMap: { [k: string]: OfflineItemStatus }, currentData: RequiredItemData[]): number[] => {
	let obsoleteIds: number[] = [];

	Object.keys(offlineItemsMap).forEach(key => {
		const id = parseInt(key, 10);
		if (!currentData.find(data => id === data.id)) {
			obsoleteIds.push(id);
		}
	});

	return obsoleteIds;
};

const deleteObsoleteItems = (requiredItemsDatas: RequiredItemData[]) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { offlineManager } = getState();
	const requiredItemsStatus = offlineManager.requiredItems;

	const obsoleteItemsIds = getObsoleteOfflineItems(requiredItemsStatus, requiredItemsDatas);

	let promises: Promise<{}>[] = obsoleteItemsIds.map(id =>
		dispatch(getOrFetchDriveItemMeta(id, false))
			.then((itemMeta: Api.DriveItem) => {
				if (requiredItemsStatus[id].isIcon) {
					return dispatch(deleteThumb(itemMeta, true));
				} else {
					return dispatch(deleteItem(itemMeta)).then(() => dispatch(removeRequiredItem(id)));
				}
			})
		);

	return Q.allSettled(promises);
};

const downloadRequiredItems = (requiredItemsDatas: RequiredItemData[]) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
	const { offlineManager, driveList } = getState();
	const { driveItems } = driveList;
	const requiredItemsStatus = offlineManager.requiredItems;

	let promises: Promise<{}>[] = requiredItemsDatas.map(item =>
		dispatch(getOrFetchDriveItemMeta(item.id, false))
			.then((itemMeta: Api.DriveItem) => {
				const isOutdated = requiredItemsStatus[item.id] && (item.url !== requiredItemsStatus[item.id].url || itemMeta.version > requiredItemsStatus[item.id].version);
				const isDownloadNeeded = !requiredItemsStatus[item.id] || isOutdated;

				if (isDownloadNeeded) {
					if (item.isIcon) {
						return dispatch(downloadThumb(item.id, DownloadPriority.High, true));
					} else {
						return dispatch(requestMobileAppDownload(itemMeta, DownloadMode.Required))
							.then(() => dispatch(updateRequiredItemStatus(itemMeta, DownloadStatus.DOWNLOADED, false, item.url)))
							.catch(() => dispatch(updateRequiredItemStatus(itemMeta, DownloadStatus.ERROR, false, item.url)));
					}
				}
				return {};
			})
			.catch(() => {
				dispatch(updateRequiredItemStatus(driveItems[item.id], DownloadStatus.ERROR, item.isIcon, item.url));
				return Promise.reject({});
			})
	);

	return Q.allSettled(promises);
};

interface RequiredItemData {
	id: number;
	isIcon: boolean;
	url?: string;
}

const getRequiredItemsDatas = (template: Api.Template): RequiredItemData[] => {
	let requiredItems: RequiredItemData[] = [];

	if (template.menu) {
		const menuFormat = Utils.getScreenFormat();
		const menu = template.menu[menuFormat] || template.menu.default;

		const getItemId = (requiredData: string, isIcon: boolean) => {
			const mediaId = Utils.getMediaId(requiredData);
			if (mediaId > 0) {
				requiredItems.push({ id: mediaId, isIcon });
			}
		};

		if (template.welcomepage && template.welcomepage.url) {
			requiredItems.push({
				id: Utils.getMediaId(template.welcomepage.url),
				url: template.welcomepage.url,
				isIcon: false
			});
		}

		Object.keys(menu).forEach(menuPosition => {
			menu[menuPosition].forEach(menuItem => {
				if (menuItem.type === MenuItemType.Page) {
					getItemId(menuItem.target, false);
				}

				if (menuItem.icon) {
					getItemId(menuItem.icon, true);
				}
			});
		});
	}

	// filter out duplicates;
	return requiredItems.filter((page, index, self) => {
		return index === self.findIndex(selfPage => {
			return selfPage.id === page.id;
		});
	});
};

const downloadDirectChildrenThumb = (driveItemId: number, level: number, previousDriveItemIds: number[]) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const { driveList, offlineManager } = getState();
	const { driveItems } = driveList;
	const { resources } = driveList.driveItems[driveItemId];
	const { thumbMap } = offlineManager;
	let promises: Q.Promise<{}>[] = [];

	if (resources) {
		resources.forEach(resource => {
			const driveItem = driveItems[resource.id];
			if (driveItem) {
				const driveItemStatus = thumbMap[resource.id];
				const isOutdated = driveItemStatus && driveItem.version > driveItemStatus.version;
				const isDownloadNeeded = (!driveItemStatus || isOutdated) && DriveItemUtils.hasIcon(driveItem);

				if (isDownloadNeeded) {
					promises.push(dispatch(downloadThumb(resource.id, DownloadPriority.Low, false)));
				}
			}
		});
	}

	return Q.allSettled(promises)
		.then(() => {
			let promises: Promise<{}>[] = [];
			if (resources) {
				resources.forEach(resource => {
					const isResourceWithChildren = resource.type === Api.DriveItemType.Directory || resource.type === Api.DriveItemType.Gallery;
					const shouldGoDeeper = isResourceWithChildren && !previousDriveItemIds.includes(resource.id);
					if (shouldGoDeeper) {
						promises.push(dispatch(downloadDirectChildrenThumb(resource.id, level + 1, [...previousDriveItemIds, driveItemId])));
					}
				});
			}
			return level === 0 ? {} : Q.allSettled(promises);
		});
};

const downloadThumb = (driveItemId: number, priority: DownloadPriority, isIcon: boolean) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
	const { galleryInfos, driveList, offlineManager } = getState();
	const { config } = galleryInfos;
	const { driveItems, resolutions } = driveList;
	const driveItem = driveItems[driveItemId];
	const { thumbMap } = offlineManager;
	const driveItemStatus = thumbMap[driveItemId];

	if (UserAgentUtils.isOnline()
		&& (DriveItemUtils.hasIcon(driveItem) || isIcon)
		&& !(driveItemStatus && (driveItemStatus.status === DownloadStatus.DOWNLOADING || driveItemStatus.status === DownloadStatus.ERROR))
	) {
		const thumbnailPath = isIcon ? DriveItemUtils.getIconOriginalPath(driveItem, config) : DriveItemUtils.getThumbnail(driveItem, config, resolutions, true);
		const extension = Utils.getFileExtension(thumbnailPath);
		const folder = isIcon ? iconsFolder : `${thumbnailsFolder}/${driveItemId}`;
		const fileName = isIcon ? `${driveItemId}.${extension}` : `card_thumbnail.${extension}`;

		return NativeApi.downloadFile(config, thumbnailPath, folder, fileName, priority)
			.promise
			.then(() => {
				return dispatch(isIcon
					? updateRequiredItemStatus(driveItem, DownloadStatus.DOWNLOADED, true)
					: updateThumbStatus(driveItemId, driveItem.version, DownloadStatus.DOWNLOADED));
			})
			.catch(() => {
				dispatch(deleteThumb(driveItem, isIcon));
				return dispatch(isIcon
					? updateRequiredItemStatus(driveItem, DownloadStatus.ERROR, true)
					: updateThumbStatus(driveItemId, driveItem.version, DownloadStatus.ERROR));
			});
	}

	return Q.Promise.reject();
};

const deleteThumb = (driveItem: Api.DriveItem, isIcon: boolean) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
	const { galleryInfos, driveList } = getState();
	const { resolutions } = driveList;

	const thumbnailPath = DriveItemUtils.getThumbnail(driveItem, galleryInfos.config, resolutions, isIcon ? false : true);
	const extension = Utils.getFileExtension(thumbnailPath);
	const path = isIcon ? `${iconsFolder}/${driveItem.id}.${extension}` : `${thumbnailsFolder}/${driveItem.id}`;

	return isIcon
		? NativeApi.existFile(path).then(fileExist => fileExist ? NativeApi.removeFile(path) : {})
		: NativeApi.existDirectory(path).then(fileExist => fileExist ? NativeApi.deleteDirJs(`../../${path}`) : {});
	};

const downloadBrandThumbIfNeeded = (configLocation: BrandThumbConfigLocation, template: Api.Template, isHome: boolean) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
		if (UserAgentUtils.isMobileApp && UserAgentUtils.isOnline()) {
			const { galleryInfos, offlineManager, appInfos } = getState();
			const thumbType = isHome ? ThumbType.HOME_THUMB : ThumbType.BRAND_THUMB;
			const logoType = isHome ? LogoType.HOME_LOGO : LogoType.BRAND_LOGO;
			const logoName = Utils.getBrandLogoName(template, logoType);
			const isMultiApp = Utils.isMultiApp(appInfos);

			const { status, filename } = offlineManager[thumbType][configLocation];

			if (logoName && (filename !== logoName || status !== DownloadStatus.DOWNLOADED)) {
				const newFilename = Utils.getFilenameFromMediaId(logoName);
				const deletePromise = filename ? Q.Promise.resolve() : NativeApi.removeFile(`${thumbnailsFolder}/brand/${filename}`);
				dispatch(updateBrandThumbStatus(configLocation, thumbType, DownloadStatus.DOWNLOADING, logoName));

				return deletePromise
					.then(() => NativeApi.downloadFile(
						galleryInfos.config,
						Utils.getOriginBrandLogoUrl(galleryInfos, isHome, isMultiApp, template),
						`${thumbnailsFolder}/brand`,
						`${newFilename}`,
						DownloadPriority.High
					).promise)
					.then(() => { dispatch(updateBrandThumbStatus(configLocation, thumbType, DownloadStatus.DOWNLOADED, logoName)); })
					.catch(() => { dispatch(updateBrandThumbStatus(configLocation, thumbType, DownloadStatus.ERROR, logoName)); });
			}
		}
		return Q.Promise.resolve();
	};

const updateBrandThumbStatus = (configLocation: BrandThumbConfigLocation, thumbType: ThumbType, status: DownloadStatus, filename?: string): UpdateBrandThumbStatusAction => ({
	type: OfflineManagerActionKeys.UPDATE_BRAND_THUMB,
	configLocation,
	thumbType,
	status,
	filename
});

const startInitialization = (): StartInitializationAction => ({ type: OfflineManagerActionKeys.START_INITIALIZATION });

const endInitialization = (): EndInitializationAction => ({ type: OfflineManagerActionKeys.END_INITIALIZATION });

const updateThumbStatus = (driveItemId: number, version: number, status: DownloadStatus): UpdateThumbStatusAction => ({
	type: OfflineManagerActionKeys.UPDATE_THUMB_STATUS,
	driveItemId,
	version,
	status
});

const updateRequiredItemStatus = (item: Api.DriveItem, status: DownloadStatus, isIcon: boolean, url?: string): UpdateRequiredItemStatusAction => ({
	type: OfflineManagerActionKeys.UPDATE_REQUIRED_ITEM_STATUS,
	version: item.version,
	id: item.id,
	status,
	isIcon,
	url
});

const removeRequiredItem = (id: number): RemoveRequiredItemAction => ({
	type: OfflineManagerActionKeys.REMOVE_REQUIRED_ITEM,
	id
});

const sendInitializationError = (): InitializationErrorAction => ({ type: OfflineManagerActionKeys.INITIALIZATION_ERROR });

export {
	OfflineManagerActionKeys, OfflineManagerAction, loadOfflineManager, initializeOfflineManager,
	downloadThumb, downloadBrandThumbIfNeeded, updateBrandThumbStatus, updateThumbStatus, sendInitializationError
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/OfflineManager/actions.ts